import {
    AssertionDescriptorDisplayValueTypeEnum,
    AssertionDescriptorValueSourceTypeEnum,
} from '../../utilities/Constants';
import { ICaseAssertionModel, ICaseModel } from '../../interfaces/ICase';

import CaseFileCheckmateSelectHelper from '../../utilities/CaseFileCheckmateSelectHelper';
import CaseHelper from '../../utilities/CaseHelper';
import CheckmateNSelect from '../shared/CheckmateNSelect';
import CheckmateSelect from '../shared/CheckmateSelect';
import CheckmateSelectHelper from '../../utilities/CheckmateSelectHelper';
import Common from '../../stores/Common';
import { CurrencyWithCentsInputControl } from '../../shared/controls/CurrencyWithCentsInput';
import { IAssertionDescriptorModel } from '../../interfaces/IAssertionDescriptor';
import { IMultiSelectOption } from '../../interfaces/ILookup';

interface IAssertionDescriptorInputControlProps {
    currentCase: ICaseModel;
    assertionDescriptors: IAssertionDescriptorModel[];
    assertionDescriptor: IAssertionDescriptorModel;
    onChangeADTextValue: (
        event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
    ) => void;
    onSearchableSingleSelectChange: (
        selectedOption: IMultiSelectOption,
        identifier: string,
        isBoolean?: boolean
    ) => void;
    onSearchableMultiSelectChange: (
        selectedOptions: IMultiSelectOption[],
        identifier: string
    ) => void;
    onSelectNarrativeCaseAssertion: (ca: ICaseAssertionModel) => void;
    readonly?: boolean;
    tabIndex?: number;
}

export function AssertionDescriptorInputControl(props: IAssertionDescriptorInputControlProps) {
    const { valueSourceType, valueDisplayType, guid } = props.assertionDescriptor;

    const firstCA = props.currentCase.caseAssertions?.find(
        (ca) => ca.assertionDescriptorGuid === props.assertionDescriptor.guid
    ) ?? { assertionDescriptorGuid: guid };

    const setStyleAsSystemAutoPopulated = CaseHelper.setStyleAsSystemAutoPopulated(
        firstCA,
        props.currentCase
    );

    const additionalStyleClass = setStyleAsSystemAutoPopulated ? ' font-green' : '';

    const handleNarrativeFieldEdit = (ca: ICaseAssertionModel) => {
        props.onSelectNarrativeCaseAssertion(
            ca ?? { assertionDescriptor: props.assertionDescriptor }
        );
    };

    if (valueSourceType?.id === AssertionDescriptorValueSourceTypeEnum.Text.Value) {
        if (valueDisplayType?.id === AssertionDescriptorDisplayValueTypeEnum.Narrative.Value) {
            return (
                <button
                    title={firstCA?.textValue}
                    className="btn btn-no-bg"
                    onClick={() => handleNarrativeFieldEdit(firstCA)}
                    tabIndex={props.tabIndex}
                    disabled={props.readonly}
                >
                    <i
                        className={
                             (firstCA?.textValue?.length ?? 0) > 0 ? 'fal fa-pen color-black' : 'fal fa-pen color-gray'
                        }
                    />
                </button>
            );
        }

        if (valueDisplayType?.id === AssertionDescriptorDisplayValueTypeEnum.DateTime.Value)
            return (
                <input
                    type="date"
                    name={props.assertionDescriptor.guid}
                    className={
                        'form-control' +
                        (firstCA?.dateTimeValue && Common.isValidDate(firstCA?.dateTimeValue)
                            ? ''
                            : ' unselectClass')
                    }
                    value={Common.dateFormat(firstCA?.dateTimeValue ?? '')}
                    onChange={props.onChangeADTextValue}
                    style={{ width: '100%' }}
                    tabIndex={props.tabIndex}
                    disabled={props.readonly}
                />
            );

        if (valueDisplayType?.id === AssertionDescriptorDisplayValueTypeEnum.Percentage.Value)
            return (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '4px',
                        alignItems: 'center',
                    }}
                >
                    <span>%</span>
                    <input
                        type="number"
                        name={props.assertionDescriptor.guid}
                        className={'form-control' + additionalStyleClass}
                        value={firstCA?.numericValue ?? ''}
                        onChange={props.onChangeADTextValue}
                        style={{ width: 'auto' }}
                        tabIndex={props.tabIndex}
                        disabled={props.readonly}
                    />
                </div>
            );

        if (
            valueDisplayType?.id === AssertionDescriptorDisplayValueTypeEnum.CurrencyWithCents.Value
        ) {
            return (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '4px',
                        alignItems: 'center',
                    }}
                >
                    <CurrencyWithCentsInputControl
                        className={'form-control'}
                        name={props.assertionDescriptor.guid}
                        onChange={props.onChangeADTextValue}
                        placeholder="Enter a valid $ amount"
                        style={{ width: 'auto' }}
                        value={firstCA?.numericValue ?? ''}
                        disabled={props.readonly}
                    />
                </div>
            );
        }

        if (
            valueDisplayType?.id === AssertionDescriptorDisplayValueTypeEnum.Currency.Value ||
            valueDisplayType?.id === AssertionDescriptorDisplayValueTypeEnum.Number.Value ||
            valueDisplayType?.id === AssertionDescriptorDisplayValueTypeEnum.Decimal.Value
        )
            return (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '4px',
                        alignItems: 'center',
                    }}
                >
                    {valueDisplayType?.id ===
                        AssertionDescriptorDisplayValueTypeEnum.Currency.Value && <span>$</span>}
                    <input
                        type="number"
                        name={props.assertionDescriptor.guid}
                        className={'form-control' + additionalStyleClass}
                        value={firstCA?.numericValue ?? ''}
                        onChange={props.onChangeADTextValue}
                        onKeyDown={(e) => {
                            if (e.key === '.' || e.key === ',' || e.key === 'e' || e.key === 'E') {
                                e.preventDefault();
                            }
                        }}
                        style={{ width: 'auto' }}
                        tabIndex={props.tabIndex}
                        disabled={props.readonly}
                    />
                </div>
            );

        if (valueDisplayType?.id === AssertionDescriptorDisplayValueTypeEnum.ShortText.Value)
            return (
                <input
                    type="text"
                    name={props.assertionDescriptor.guid}
                    className={'form-control' + additionalStyleClass}
                    value={firstCA?.textValue ?? ''}
                    onChange={props.onChangeADTextValue}
                    style={{ width: 'auto' }}
                    tabIndex={props.tabIndex}
                    disabled={props.readonly}
                />
            );

        if (valueDisplayType?.id === AssertionDescriptorDisplayValueTypeEnum.SSN.Value)
            return (
                <input
                    style={{ width: '30%' }}
                    className={'form-control' + additionalStyleClass}
                    type="text"
                    placeholder="XXX-XX-XXXX"
                    maxLength={11}
                    name={props.assertionDescriptor.guid}
                    value={firstCA?.textValue}
                    onChange={props.onChangeADTextValue}
                    tabIndex={props.tabIndex}
                    disabled={props.readonly}
                />
            );
    }

    if (valueSourceType?.id === AssertionDescriptorValueSourceTypeEnum.Boolean.Value) {
        return (
            <div style={{ minWidth: 150, maxWidth: 150, width: '100%' }}>
                <CheckmateSelect
                    options={CheckmateSelectHelper.getBooleanOptions()}
                    systemPopulated={setStyleAsSystemAutoPopulated}
                    name={props.assertionDescriptor.guid!}
                    value={CheckmateSelectHelper.getBooleanOptionValue(firstCA?.booleanValue)}
                    onChange={(selectedOption: IMultiSelectOption) =>
                        props.onSearchableSingleSelectChange(
                            selectedOption,
                            props.assertionDescriptor.guid!,
                            true
                        )
                    }
                    tabIndex={props.tabIndex}
                    isDisabled={props.readonly}
                />
            </div>
        );
    }

    if (valueSourceType?.id === AssertionDescriptorValueSourceTypeEnum.Selection.Value) {
        if (
            props.assertionDescriptor.valueDisplayType?.id ===
            AssertionDescriptorDisplayValueTypeEnum.MultiSelect.Value
        ) {
            return (
                <div style={{ minWidth: 250, maxWidth: 300, width: '100%' }}>
                    <CheckmateNSelect
                        options={CaseFileCheckmateSelectHelper.getOptionsForCheckmateSelect(
                            props.assertionDescriptor.assertionDescriptorValues!
                        )}
                        value={CaseFileCheckmateSelectHelper.getCheckmateNSelectValue(
                            props.currentCase.caseAssertions?.filter(
                                (ca) =>
                                    ca.assertionDescriptorGuid === props.assertionDescriptor.guid
                            ) ?? [],
                            props.assertionDescriptors
                        )}
                        onChange={(options: IMultiSelectOption[]) => {
                            props.onSearchableMultiSelectChange(
                                options,
                                props.assertionDescriptor.guid!
                            );
                        }}
                        disabled={props.readonly}
                    />
                </div>
            );
        } else {
            return (
                <div style={{ minWidth: 250, maxWidth: 300, width: '100%' }}>
                    <CheckmateSelect
                        options={CaseFileCheckmateSelectHelper.getOptionsForCheckmateSelect(
                            props.assertionDescriptor.assertionDescriptorValues ?? []
                        )}
                        systemPopulated={setStyleAsSystemAutoPopulated}
                        name={props.assertionDescriptor.guid!}
                        value={CaseFileCheckmateSelectHelper.getCheckmateSelectValue(
                            firstCA,
                            props.assertionDescriptors
                        )}
                        onChange={(options: any) => {
                            props.onSearchableSingleSelectChange(
                                options,
                                props.assertionDescriptor.guid!
                            );
                        }}
                        tabIndex={props.tabIndex}
                        isDisabled={props.readonly}
                    />
                </div>
            );
        }
    }

    return null;
}
